import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { Button, Space, Spinner, Row } from "@masterteam/mt-design-react";
import Table from "../NewTable";

import api from "../../providers/api";
import DeleteModal from "../DeleteModal";
import { addToast, formatDate } from "../../helpers";

import EditForm from "./EditForm";

import { ReactComponent as EditIcon } from "../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete.svg";
import { ReactComponent as AddIcon } from "../../assets/images/plus-symbol.svg";

import "./style.scss";

const initialModalState = {
  isOpen: false,
  data: null,
  loading: false
};

function Delegations() {
  const userData = useSelector(({ user }) => user.user);
  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [formModal, setFormModal] = useState(initialModalState);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    getAllDelegations();
    getAllUsers();
  }, []);

  const getAllUsers = () => {
    api.user
      .getAll()
      .then(res => {
        const { StatusCode, Data } = res;
        if (StatusCode === "Success") {
          setUsers(Data.filter(user => user.UserName !== userData.UserName));
        }
      })
      .catch(err => {
        return err;
      });
  };

  const getAllDelegations = () => {
    setLoading(true);
    api.delegation
      .getAll()
      .then(res => {
        if (res.StatusCode === "Success") {
          setDelegations(res.Data);
        }
      })
      .catch(err => {
        addToast.error(err.message, {
          toastId: "get-users-error"
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteItem = id => {
    setDeleteModal(currentModal => ({ ...currentModal, loading: true }));
    api.delegation
      .delete(id)
      .then(res => {
        if (res?.StatusCode === "Success") {
          const array = [...delegations];
          const index = array.findIndex(item => item.ID === id);
          if (index !== -1) {
            array.splice(index, 1);
            setDelegations(array);
            addToast.success("toast.successDelete");
          }
        }
      })
      .catch(err => {
        return err;
      })
      .finally(() => {
        setDeleteModal(initialModalState);
      });
  };

  const updateList = (item, action) => {
    setDelegations(currentDelegations => {
      const temp = [...currentDelegations];
      if (action === "create") {
        temp.push(item);
      } else {
        const index = temp.findIndex(element => element.ID === item.ID);
        if (index !== -1) {
          temp[index] = item;
        }
      }
      return temp;
    });
  };

  const columns = [
    {
      key: "to",
      header: <FormattedMessage id="manage-to" />,
      cell: item => item.ToUser.DisplayName,
      width: "50%"
    },
    {
      key: "startDate",
      header: <FormattedMessage id="delegations.start-date" />,
      cell: item => formatDate(item.FromDate),
      width: "20%"
    },
    {
      key: "endDate",
      header: <FormattedMessage id="delegations.end-date" />,
      cell: item => formatDate(item.ToDate),
      width: "20%"
    }
  ];

  const actions = [
    {
      key: "edit",
      variant: "primary",
      Icon: EditIcon,
      "tooltip-position": "buttom",
      tooltip: "Update",
      onClick: item => {
        setFormModal({
          isOpen: true,
          data: item
        });
      }
    },
    {
      key: "delete",
      variant: "error",
      Icon: DeleteIcon,
      "tooltip-position": "buttom",
      tooltip: "Delete",
      onClick: item => {
        setDeleteModal({
          isOpen: true,
          data: item.ID,
          loading: false
        });
      }
    }
  ];

  return (
    <Space direction="vertical" size="large" className="delegations-container">
      <Row align="center" justify="between" className="del-header">
        <FormattedMessage id="manage-delegation" />
        <Button
          size="large"
          Icon={AddIcon}
          onClick={() =>
            setFormModal({
              isOpen: true,
              data: null,
              loading: false
            })
          }
        >
          <FormattedMessage id="common.add" />
        </Button>
      </Row>
      <div className="delegations-data">
        {loading ? (
          <div className="loder-container">
            <Spinner size="large" />
          </div>
        ) : (
          <Table data={delegations} columns={columns} actions={actions} />
        )}
      </div>

      {formModal.isOpen ? (
        <EditForm
          data={formModal.data}
          users={users}
          callback={updateList}
          closeModal={() => setFormModal(initialModalState)}
        />
      ) : null}

      {deleteModal.isOpen && (
        <DeleteModal
          isOpen={deleteModal.isOpen}
          title="Delegation"
          closeModal={() => setDeleteModal(initialModalState)}
          handleDelete={() => deleteItem(deleteModal.data)}
          ButtonStatus={deleteModal.buttonStatus}
        />
      )}
    </Space>
  );
}

export default Delegations;
